import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Renders a thin sticky red banner across the top of public pages whenever
 * the restaurant is closed (manual pause OR outside opening hours OR holiday).
 *
 * Polls every 60s so admins can flip the kill-switch and customers see the
 * banner appear without a page refresh. Renders nothing while we're in an
 * open/preorder state — keeps the home page completely clean by default.
 */
export const RestaurantStatusBanner = () => {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const r = await axios.get(`${API_URL}/api/restaurant/status`);
        if (!cancelled) setStatus(r.data);
      } catch (err) {
        // Silent fail — we don't want a broken status endpoint to break the page.
        if (!cancelled) setStatus(null);
      }
    };
    fetchStatus();
    const id = setInterval(fetchStatus, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Render only when the restaurant is fully closed. Preorder window is
  // handled by the checkout page itself; we don't want a scary red banner
  // sitting on the menu when customers can still place preorders.
  if (!status || status.status !== 'closed') return null;

  return (
    <div
      className="bg-red-600 text-white text-sm py-2 px-4 flex items-center justify-center gap-2"
      data-testid="restaurant-closed-banner"
      role="alert"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span className="text-center">
        {status.message || "We're not taking orders right now."}
      </span>
    </div>
  );
};

export default RestaurantStatusBanner;
